import React from "react";
import { Image, Text, View, StyleSheet } from "@react-pdf/renderer";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import type { ProgramRankingPdfRow } from "../programRanking/types";
import type { MetricsStreamingFormat } from "../miContenidoExportData";
import {
  fmtCompactPlatform,
  fmtLiveMetric,
} from "../metricsReportWeb/buildPeriodSummaryData";
import { metricsStreamingLabels } from "../metricsReportWeb/metricsStreamingLabels";
import {
  rankCardsPageLayout,
  type RankCardsDensity,
} from "../rankCardsPageLayout";
import { usePdfTheme } from "./PdfThemeContext";
import {
  PDF_CARD_BORDER_WIDTH,
  PDF_CARD_RADIUS,
  PDF_FONT,
  PDF_FONT_FAMILY,
} from "./pdfTheme";
import {
  getRankingPodscopeCaptionParts,
  formatRankingPodscopeMissingRankExplainer,
} from "./formatHelpers";

const METRIC_LABEL_LINE_HEIGHT = 1.2;
const NAME_LINE_HEIGHT = 1.1;
const CARD_GAP = 16;

type DensityTokens = {
  cardPadding: number;
  rowGap: number;
  identityMarginBottom: number;
  identityGap: number;
  avatarSize: number;
  avatarRadius: number;
  avatarInitialsSize: number;
  rankFontSize: number;
  nameFontSize: number;
  nameLineCount: number;
  metricsPaddingTop: number;
  metricsMarginTop: number;
  metricRowGap: number;
  metricLabelFontSize: number;
  metricLabelMarginBottom: number;
  metricValueFontSize: number;
  captionMarginTop: number;
  forceSingleLineLabels: boolean;
};

const DENSITY_TOKENS: Record<RankCardsDensity, DensityTokens> = {
  comfortable: {
    cardPadding: 24,
    rowGap: 16,
    identityMarginBottom: 14,
    identityGap: 12,
    avatarSize: 52,
    avatarRadius: 8,
    avatarInitialsSize: 15,
    rankFontSize: 22,
    nameFontSize: 15,
    nameLineCount: 3,
    metricsPaddingTop: 12,
    metricsMarginTop: 14,
    metricRowGap: 12,
    metricLabelFontSize: 8,
    metricLabelMarginBottom: 3,
    metricValueFontSize: 15,
    captionMarginTop: 14,
    forceSingleLineLabels: false,
  },
  compact: {
    cardPadding: 10,
    rowGap: 10,
    identityMarginBottom: 8,
    identityGap: 8,
    avatarSize: 32,
    avatarRadius: 6,
    avatarInitialsSize: 11,
    rankFontSize: 16,
    nameFontSize: 11,
    nameLineCount: 2,
    metricsPaddingTop: 8,
    metricsMarginTop: 8,
    metricRowGap: 6,
    metricLabelFontSize: 7,
    metricLabelMarginBottom: 2,
    metricValueFontSize: 11,
    captionMarginTop: 10,
    forceSingleLineLabels: true,
  },
};

/** "Prom. estreno" → ["PROM.", "ESTRENO"] for reliable two-line PDF labels. */
function promAvgLabelLines(
  format: MetricsStreamingFormat,
  singleLine: boolean,
): string | readonly [string] | readonly [string, string] {
  const promAvg = metricsStreamingLabels(format).promAvg;
  if (singleLine) {
    return promAvg.toUpperCase();
  }
  if (promAvg.startsWith("Prom. ")) {
    return [i18n.t("metricsPdf:promPrefix"), promAvg.slice("Prom. ".length).toUpperCase()];
  }
  return [promAvg.toUpperCase()];
}

function rankingPodscopeLabel(
  singleLine: boolean,
): string | readonly [string, string] {
  return singleLine
    ? i18n.t("metricsPdf:rankPodscopeSingle")
    : [i18n.t("metricsPdf:rankLine1"), i18n.t("metricsPdf:rankLine2")];
}

const AVATAR_PALETTE = [
  "#2b5f8a",
  "#8a2b4f",
  "#2b8a5f",
  "#6a4b8a",
  "#8a6a2b",
  "#8a4f2b",
] as const;

const STOP_WORDS = new Set([
  "el",
  "la",
  "los",
  "las",
  "de",
  "del",
  "en",
  "y",
  "a",
  "un",
  "una",
]);

function programInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => !STOP_WORDS.has(w.toLowerCase()));
  if (words.length >= 2) {
    return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`.toUpperCase();
  }
  return (words[0] ?? name).slice(0, 2).toUpperCase();
}

function programAvatarColor(programId: string): string {
  let hash = 0;
  for (let i = 0; i < programId.length; i++) {
    hash = (hash * 31 + programId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

function useProgramRankCardStyles(density: RankCardsDensity) {
  const theme = usePdfTheme();
  const tokens = DENSITY_TOKENS[density];
  const nameBoxHeight =
    tokens.nameFontSize * NAME_LINE_HEIGHT * tokens.nameLineCount;
  const metricLabelOneLineMinH =
    tokens.metricLabelFontSize * METRIC_LABEL_LINE_HEIGHT;
  const metricLabelTwoLineMinH = metricLabelOneLineMinH * 2;

  return React.useMemo(
    () =>
      StyleSheet.create({
        root: {
          width: "100%",
          flex: 1,
          minHeight: 0,
          flexDirection: "column",
          fontFamily: PDF_FONT_FAMILY,
        },
        grid: {
          flex: 1,
          minHeight: 0,
          flexDirection: "column",
          width: "100%",
        },
        gridRow: {
          flex: 1,
          minHeight: 0,
          flexDirection: "row",
          width: "100%",
          marginBottom: tokens.rowGap,
        },
        gridRowLast: {
          marginBottom: 0,
        },
        card: {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          borderWidth: PDF_CARD_BORDER_WIDTH,
          borderColor: theme.cardBorder,
          borderRadius: PDF_CARD_RADIUS,
          backgroundColor: theme.cardBg,
          marginRight: CARD_GAP,
          fontFamily: PDF_FONT_FAMILY,
        },
        cardLast: {
          marginRight: 0,
        },
        cardBody: {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          paddingTop: tokens.cardPadding,
          paddingRight: tokens.cardPadding,
          paddingBottom: tokens.cardPadding,
          paddingLeft: tokens.cardPadding,
          flexDirection: "column",
        },
        cardSpacer: {
          flex: 1,
          minWidth: 0,
          marginRight: CARD_GAP,
        },
        cardSpacerLast: {
          marginRight: 0,
        },
        rank: {
          fontSize: tokens.rankFontSize,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          marginRight: tokens.identityGap,
          flexShrink: 0,
          fontFamily: PDF_FONT_FAMILY,
        },
        identity: {
          flexDirection: "row",
          alignItems: "center",
          marginBottom: tokens.identityMarginBottom,
        },
        avatar: {
          width: tokens.avatarSize,
          height: tokens.avatarSize,
          borderRadius: tokens.avatarRadius,
          marginRight: tokens.identityGap,
          objectFit: "cover",
        },
        avatarPlaceholder: {
          width: tokens.avatarSize,
          height: tokens.avatarSize,
          borderRadius: tokens.avatarRadius,
          marginRight: tokens.identityGap,
          alignItems: "center",
          justifyContent: "center",
        },
        avatarInitials: {
          fontSize: tokens.avatarInitialsSize,
          fontWeight: PDF_FONT.bold,
          color: theme.textOnAccent,
          fontFamily: PDF_FONT_FAMILY,
        },
        nameWrap: {
          flex: 1,
          minWidth: 0,
          height: nameBoxHeight,
          justifyContent: "center",
        },
        name: {
          fontSize: tokens.nameFontSize,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          lineHeight: NAME_LINE_HEIGHT,
          fontFamily: PDF_FONT_FAMILY,
        },
        metrics: {
          borderTopWidth: 1,
          borderTopColor: theme.rowDivider,
          paddingTop: tokens.metricsPaddingTop,
          marginTop: tokens.metricsMarginTop,
        },
        metricRow: {
          flexDirection: "row",
          marginBottom: tokens.metricRowGap,
        },
        metricRowLast: {
          marginBottom: 0,
        },
        metricCell: {
          flex: 1,
          minWidth: 0,
        },
        metricCellRight: {
          alignItems: "flex-end",
        },
        metricLabel: {
          fontSize: tokens.metricLabelFontSize,
          fontWeight: PDF_FONT.semibold,
          color: theme.textMutedAlt,
          lineHeight: METRIC_LABEL_LINE_HEIGHT,
          fontFamily: PDF_FONT_FAMILY,
        },
        metricLabelWrap: {
          minHeight: metricLabelOneLineMinH,
          marginBottom: tokens.metricLabelMarginBottom,
          justifyContent: "flex-start",
        },
        metricLabelWrapTwoLine: {
          minHeight: metricLabelTwoLineMinH,
        },
        metricValue: {
          fontSize: tokens.metricValueFontSize,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          fontFamily: PDF_FONT_FAMILY,
        },
        caption: {
          marginTop: tokens.captionMarginTop,
          flexShrink: 0,
          flexDirection: "column",
          gap: 2,
        },
        captionLine: {
          fontSize: 11,
          lineHeight: 1.4,
          color: theme.textMutedAlt,
          fontFamily: PDF_FONT_FAMILY,
        },
        captionDate: {
          fontWeight: PDF_FONT.bold,
          color: theme.textMutedAlt,
        },
      }),
    [theme, tokens, nameBoxHeight, metricLabelOneLineMinH, metricLabelTwoLineMinH],
  );
}

type ProgramRankCardStyles = ReturnType<typeof useProgramRankCardStyles>;

function MetricBlock({
  label,
  value,
  align = "left",
  styles,
}: {
  /** One string, or one/two line tuples (e.g. PROM. / ESTRENO). */
  label: string | readonly [string] | readonly [string, string];
  value: string;
  align?: "left" | "right";
  styles: ProgramRankCardStyles;
}) {
  const lines: string[] = Array.isArray(label) ? [...label] : [label];
  const twoLine = lines.length > 1;
  const alignRight = align === "right" ? { textAlign: "right" as const } : null;

  return (
    <View
      style={[
        styles.metricCell,
        ...(align === "right" ? [styles.metricCellRight] : []),
      ]}
    >
      <View
        style={[
          styles.metricLabelWrap,
          ...(twoLine ? [styles.metricLabelWrapTwoLine] : []),
          ...(align === "right" ? [styles.metricCellRight] : []),
        ]}
      >
        {lines.map((line) => (
          <Text
            key={line}
            wrap={false}
            style={[styles.metricLabel, ...(alignRight ? [alignRight] : [])]}
          >
            {line}
          </Text>
        ))}
      </View>
      <Text style={styles.metricValue}>{value}</Text>
    </View>
  );
}

function ProgramAvatar({
  row,
  styles,
}: {
  row: ProgramRankingPdfRow;
  styles: ProgramRankCardStyles;
}) {
  const bg = programAvatarColor(row.programId);
  if (row.imageUrl) {
    return <Image src={row.imageUrl} style={styles.avatar} />;
  }
  return (
    <View style={[styles.avatarPlaceholder, { backgroundColor: bg }]}>
      <Text style={styles.avatarInitials}>{programInitials(row.name)}</Text>
    </View>
  );
}

function ProgramRankCard({
  row,
  periodStreamingFormat,
  isLast,
  styles,
  singleLineLabels,
}: {
  row: ProgramRankingPdfRow;
  periodStreamingFormat: MetricsStreamingFormat;
  isLast?: boolean;
  styles: ProgramRankCardStyles;
  singleLineLabels: boolean;
}) {
  const { t } = useTranslation("metricsPdf");
  const promLabel = promAvgLabelLines(
    row.streamingFormat ?? periodStreamingFormat,
    singleLineLabels,
  );

  return (
    <View
      style={[styles.card, ...(isLast ? [styles.cardLast] : [])]}
      wrap={false}
    >
      <View style={styles.cardBody}>
        <View style={styles.identity}>
          <Text style={styles.rank}>#{row.rank}</Text>
          <ProgramAvatar row={row} styles={styles} />
          <View style={styles.nameWrap}>
            <Text style={styles.name} hyphenationCallback={(word) => [word]}>
              {row.name}
            </Text>
          </View>
        </View>
        <View style={styles.metrics}>
          <View style={styles.metricRow}>
            <MetricBlock
              label={t("viewsCaps")}
              value={
                row.totalViews > 0 ? fmtCompactPlatform(row.totalViews) : "—"
              }
              styles={styles}
            />
            <MetricBlock
              label={t("viewsAvgLabel")}
              value={row.avgViews > 0 ? fmtCompactPlatform(row.avgViews) : "—"}
              align="right"
              styles={styles}
            />
          </View>
          <View style={styles.metricRow}>
            <MetricBlock
              label={t("intLabel")}
              value={
                row.totalInteractions > 0
                  ? fmtCompactPlatform(row.totalInteractions)
                  : "—"
              }
              styles={styles}
            />
            <MetricBlock
              label={t("intAvgLabel")}
              value={
                row.avgInteractions > 0
                  ? fmtCompactPlatform(row.avgInteractions)
                  : "—"
              }
              align="right"
              styles={styles}
            />
          </View>
          <View style={[styles.metricRow, styles.metricRowLast]}>
            <MetricBlock
              label={promLabel}
              value={fmtLiveMetric(row.avgViewers)}
              styles={styles}
            />
            <MetricBlock
              label={rankingPodscopeLabel(singleLineLabels)}
              value={
                row.rankPodscope != null && row.rankPodscope > 0
                  ? `#${row.rankPodscope}`
                  : "—"
              }
              align="right"
              styles={styles}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

type Props = {
  /** Already sorted + ranked rows for this summary slide (do not re-sort). */
  rows: ProgramRankingPdfRow[];
  rankingPeriod?: { startDate: string; endDate: string } | null;
  podscopeActiveProgramCount?: number | null;
  streamingFormat?: MetricsStreamingFormat;
};

export function ProgramRankCardsPdfSection({
  rows,
  rankingPeriod = null,
  podscopeActiveProgramCount = null,
  streamingFormat = "live",
}: Props): React.ReactElement | null {
  const { cols, density } = rankCardsPageLayout(rows.length);
  const tokens = DENSITY_TOKENS[density];
  const styles = useProgramRankCardStyles(density);
  if (rows.length === 0) return null;

  const { title, body } = getRankingPodscopeCaptionParts({
    rankingPeriod,
    podscopeActiveProgramCount,
  });
  const showMissingRankExplainer = rows.some(
    (row) => row.rankPodscope == null || row.rankPodscope <= 0,
  );

  const rowChunks: ProgramRankingPdfRow[][] = [];
  for (let i = 0; i < rows.length; i += cols) {
    rowChunks.push(rows.slice(i, i + cols));
  }

  return (
    <View style={styles.root}>
      <View style={styles.grid}>
        {rowChunks.map((chunk, rowIndex) => {
          const isLastRow = rowIndex === rowChunks.length - 1;
          const spacers = cols - chunk.length;
          return (
            <View
              key={`row-${rowIndex}`}
              style={[
                styles.gridRow,
                ...(isLastRow ? [styles.gridRowLast] : []),
              ]}
            >
              {chunk.map((row, colIndex) => (
                <ProgramRankCard
                  key={row.programId}
                  row={row}
                  periodStreamingFormat={streamingFormat}
                  isLast={colIndex === cols - 1}
                  styles={styles}
                  singleLineLabels={tokens.forceSingleLineLabels}
                />
              ))}
              {Array.from({ length: spacers }).map((_, spacerIndex) => {
                const colIndex = chunk.length + spacerIndex;
                return (
                  <View
                    key={`spacer-${rowIndex}-${spacerIndex}`}
                    style={[
                      styles.cardSpacer,
                      ...(colIndex === cols - 1
                        ? [styles.cardSpacerLast]
                        : []),
                    ]}
                  />
                );
              })}
            </View>
          );
        })}
      </View>
      <View style={styles.caption}>
        <Text style={styles.captionLine}>
          <Text style={styles.captionDate}>* {title}: </Text>
          {body}
        </Text>
        {showMissingRankExplainer ? (
          <Text style={styles.captionLine}>
            <Text style={styles.captionDate}>—: </Text>
            {formatRankingPodscopeMissingRankExplainer(rankingPeriod)}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
